import { useEffect, useState, type ReactElement } from 'react';

import { apiUrl } from '../lib/build';
import { explainFork } from './reviewExplain';
import { logoOf } from './venues';
import './reviews.css';

interface ShownLeg {
  book: string;
  side: string;
  odds: string;
  stake?: string;
  code?: string;
}

interface Shown {
  match: string;
  sport: string;
  market?: string;
  roi: string;
  gain?: string;
  at?: string;
  legs: ShownLeg[];
}

export function ReviewPage(): ReactElement {
  const id = location.pathname.replace(/\/+$/, '').split('/').pop() ?? '';
  const [card, setCard] = useState<Shown | null>(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    void fetch(apiUrl(`/v1/reviews/${encodeURIComponent(id)}`), { credentials: 'same-origin' })
      .then(async (response) => {
        if (!response.ok) {
          setMissing(true);
          return;
        }
        setCard((await response.json()) as Shown);
      })
      .catch(() => setMissing(true));
  }, [id]);
  if (missing) {
    return (
      <main className="nx-root rv">
        <p className="rv__miss">Этой вилки по ссылке нет.</p>
      </main>
    );
  }
  if (card === null) {
    return (
      <main className="nx-root rv">
        <p className="rv__miss">Открываю вилку.</p>
      </main>
    );
  }
  const sum = explainFork(card.legs);
  const percent = card.roi.startsWith('-') || card.roi.startsWith('+') ? `${card.roi}%` : `+${card.roi}%`;
  const [first, second] = card.legs;
  return (
    <main className="nx-root rv">
      <p className="rv__when">{card.at ? `Результат ученика от ${card.at}` : 'Результат ученика'}</p>
      <article className="nx-card">
        <div className="nx-hold__who">
          {card.sport ? <span>{card.sport}</span> : null}
          {card.sport ? <i>/</i> : null}
          <b>{card.match}</b>
          {card.market ? <i>/</i> : null}
          {card.market ? <span>{card.market}</span> : null}
        </div>
        <ol className="nx-legs">
          {card.legs.slice(0, 2).map((leg, index) => {
            const logo = logoOf(leg.code || leg.book);
            return (
            <li key={`${leg.book}-${leg.side}`} className={index === 0 ? 'nx-leg nx-leg--go' : 'nx-leg nx-leg--then'}>
              <span className="nx-leg__n">{index + 1}</span>
              <span className="nx-leg__at">
                {logo ? <img className="nx-leg__mk" src={logo} alt="" /> : null}
                <b className="nx-leg__book">{leg.book}</b>
              </span>
              <span className="nx-leg__sum">
                {sum ? sum.legs[index]?.stake.replace(' $', '') : leg.stake || '—'}
                <i>$</i>
              </span>
              <span className="nx-leg__pick">{leg.side}</span>
              <span className="nx-leg__px">кэф {leg.odds}</span>
            </li>
            );
          })}
        </ol>
        {sum && first && second ? (
          <div className="nx-split">
            <div className="nx-split__bar" style={{ ['--w' as string]: String(sum.share) }}>
              <i />
              <i />
            </div>
            <div className="nx-split__legend">
              <span>{first.book} <b>{sum.legs[0].stake}</b></span>
              <span>{second.book} <b>{sum.legs[1].stake}</b></span>
            </div>
          </div>
        ) : null}
        <div className="nx-tot">
          {sum ? (
            <span>списалось <b>{sum.staked}</b></span>
          ) : null}
          {sum ? (
            <span className="win">вернулось <b>{sum.back}</b></span>
          ) : null}
          <span className="gain">{percent}</span>
        </div>
      </article>
      <section className="rv__how">
        <h2>Как получилось заработать с этой вилки?</h2>
        {sum && first && second ? (
          <>
            <p>
              Поставили <b>{sum.legs[0].stake}</b> на {first.book} по кэфу <b>{first.odds}</b>.
              Если заходит эта нога, возвращается <b>{sum.legs[0].back}</b>.
            </p>
            <p>
              Поставили <b>{sum.legs[1].stake}</b> на {second.book} по кэфу <b>{second.odds}</b>.
              Если заходит эта нога, возвращается <b>{sum.legs[1].back}</b>.
            </p>
            <p>
              На обе ноги ушло <b>{sum.staked}</b>. Вилка платит по худшей ноге:
              {' '}<b>{sum.back}</b> минус поставленные <b>{sum.staked}</b> — это <b>+{sum.profit}</b>,
              {' '}или <b>{percent}</b>.
            </p>
          </>
        ) : (
          <p>
            Плюс этой вилки — <b>{percent}</b>{card.gain ? `, ${card.gain}` : ''}.
          </p>
        )}
      </section>
    </main>
  );
}
